import React, { useMemo } from 'react'
import { Box, Popover, Typography, Divider, styled } from '@mui/material'
import { Timeline as RouteIcon, Speed as SpeedIcon, AccessTime as TimeIcon, Person as DriverIcon } from '@mui/icons-material'
import { convertDateTimeToPreferedTimezone } from 'data/dateTime/convertDateTimeToPreferedTimezone'
import T from 'T'

const TooltipHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
}))

const TooltipContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
}))

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1),
  '&:last-child': { marginBottom: 0 },
}))

const LabelGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.text.secondary,
  gap: theme.spacing(1),
  '& svg': { fontSize: 18 },
}))

const ValueText = styled(Typography)(({ theme, isAlert }) => ({
  fontWeight: 500,
  color: isAlert ? theme.palette.error.main : theme.palette.text.primary,
  maxWidth: 180,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}))

const formatSpeed = val => {
  if (!val) return '0 mph'
  return `${Math.round(val)} mph`
}

const BreadcrumbHoverInfo = ({ info }) => {
  const formattedTime = useMemo(() => {
    if (!info?.timestamp) return ''
    const dateObj = new Date(info.timestamp)
    if (isNaN(dateObj.getTime())) return ''

    const isoTimestamp = dateObj.toISOString()
    const { fullDateTime } = convertDateTimeToPreferedTimezone({ dateTime: isoTimestamp })
    return fullDateTime
  }, [info?.timestamp])

  if (!info) return null
  const { x, y, routeName, speed, driverName } = info

  // Prevent rendering at (0,0) or with invalid coordinates
  if (!Number.isFinite(x) || !Number.isFinite(y) || (x === 0 && y === 0)) {
    return null
  }

  return (
    <Popover
      open
      anchorReference="anchorPosition"
      anchorPosition={{ top: y, left: x }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          sx: {
            mt: 2,
            ml: 2,
            borderRadius: 1,
            minWidth: 280,
            padding: 0,
            bgcolor: 'background.paper',
            pointerEvents: 'none',
          },
        },
      }}
      sx={{ pointerEvents: 'none' }}
      disableRestoreFocus
      disableAutoFocus
      disableEnforceFocus
      hideBackdrop
      disableScrollLock
      style={{ pointerEvents: 'none', zIndex: 9999 }}
    >
      <TooltipHeader>
        <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
          {T.DRIVER_PATH_INFO}
        </Typography>
      </TooltipHeader>
      <Divider />
      <TooltipContent>
        <InfoRow>
          <LabelGroup>
            <RouteIcon />
            <Typography variant="body2">{T.ROUTE_NAME}</Typography>
          </LabelGroup>
          <ValueText variant="body2">{routeName}</ValueText>
        </InfoRow>

        <InfoRow>
          <LabelGroup>
            <DriverIcon />
            <Typography variant="body2">{T.DRIVER}</Typography>
          </LabelGroup>
          <ValueText variant="body2" title={driverName}>
            {driverName || T.UNASSIGNED}
          </ValueText>
        </InfoRow>

        <InfoRow>
          <LabelGroup>
            <SpeedIcon />
            <Typography variant="body2">{T.SPEED}</Typography>
          </LabelGroup>
          <ValueText variant="body2" isAlert={speed > 80}>
            {formatSpeed(speed)}
          </ValueText>
        </InfoRow>

        <InfoRow>
          <LabelGroup>
            <TimeIcon />
            <Typography variant="body2">{T.TIMESTAMP}</Typography>
          </LabelGroup>
          <ValueText variant="body2">{formattedTime}</ValueText>
        </InfoRow>
      </TooltipContent>
    </Popover>
  )
}

export default BreadcrumbHoverInfo
